import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { TourProvider, useTour } from '@reactour/tour';
import { Chip, colors } from '@mui/material';

const TourLauncherCtx = createContext(null);

// ---------- Shared TourLauncher Context ----------
function TourLauncherProvider({ children }) {
  const currentStarterRef = useRef(null);

  const register = useCallback((starterFn) => {
    currentStarterRef.current = starterFn;
    return () => {
      if (currentStarterRef.current === starterFn) {
        currentStarterRef.current = null;
      }
    };
  }, []);

  const launch = useCallback(() => {
    if (typeof currentStarterRef.current === 'function') {
      currentStarterRef.current();
    }
  }, []);

  return (
    <TourLauncherCtx.Provider value={{ register, launch }}>
      {children}
    </TourLauncherCtx.Provider>
  );
}

export function useTourLauncher() {
  const ctx = useContext(TourLauncherCtx);
  if (!ctx)
    throw new Error('useTourLauncher must be used within TourLauncherProvider');
  return ctx;
}

// ---------- Reactour styles ----------
const chipSampleColors = {
  '0': 'rgba(19,130,117,1)',
  '1': '#ffffff',
};

const sharedChipSx = {
  fontSize: '.8rem',
  marginTop: '0rem',
  marginBottom: 0,
  padding: '0.1px',
  maxHeight: '28px',
  height: '20px',
  '& .MuiChip-label': {
    paddingTop: 0,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '20px',
    transform: "translateY(0.5px)", 
  },
};

const styles = {
  popover: (base) => ({
    ...base,
    zIndex: 22000,
    pointerEvents: 'auto',
    paddingTop: 34,
    fontFamily: 'Inter, sans-serif',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    textRendering: 'optimizeLegibility',
  }),
  dot: (base, { current }) => ({
    ...base, 
    background: current ? 'rgba(19,130,117,1)' : '#cccccc',
  }),
  close: (base) => ({
    ...base, 
    color: 'rgba(19,130,117,1)',
  }),
  maskWrapper: (base) => ({ ...base, pointerEvents: 'none' }),
  maskArea: (base) => ({ ...base, pointerEvents: 'none' }),
};

// Custom Navigation with Dots and Buttons
function CustomNavigation({ steps, currentStep, setCurrentStep, setIsOpen }) {
  const isLastStep = currentStep === steps.length - 1;
  
  const handleNext = () => {
    if (isLastStep) {
      setIsOpen(false);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(Math.max(0, currentStep - 1));
  };

  return (
    <div className="reactour__custom-navigation" style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
      {/* Dots Progress Indicator */}
      <div className="reactour__dots" style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              padding: 0, 
              border: 'none',
              cursor: 'pointer',
              backgroundColor: currentStep === index ? 'rgba(19,130,117,1)' : '#cccccc',
              transition: 'background-color 0.3s ease'
            }}
            aria-label={`Go to step ${index + 1}`}
          />
        ))}
      </div>

      {/* Navigation Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {currentStep > 0 ? (
          <button 
            onClick={handlePrev}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              border: '1px solid #00695c',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#00695c',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Previous
          </button>
        ) : (
          <div></div> // Empty div for spacing when no Previous button
        )}
        
        <button 
          onClick={handleNext}
          style={{
            padding: '8px 24px',
            backgroundColor: 'rgba(19,130,117,1)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '80px'
          }}
        >
          {isLastStep ? 'Done' : 'Next'}
        </button>
      </div>
    </div>
  );
}

// Custom component to handle outside clicks
function TourClickHandler() {
  const { isOpen, setIsOpen } = useTour();

  useEffect(() => {
    if (!isOpen) return;

    let rafId = 0;

    const applyCrispPosition = () => {
      const el =
        document.querySelector('[data-tour-elem="popover"]') ||
        document.querySelector('.reactour__popover');

      if (el) {
        const cs = window.getComputedStyle(el);
        const t = cs.transform;

        if (t && t !== 'none') {
          let tx = 0;
          let ty = 0;

          const m = t.match(/matrix\(([^)]+)\)/);
          if (m) {
            const parts = m[1].split(',').map(s => parseFloat(s.trim()));
            tx = parts[4] || 0;
            ty = parts[5] || 0;
          } else {
            const tr = t.match(/translate(?:3d)?\(\s*([-0-9.]+)px(?:,\s*([-0-9.]+)px)?/);
            if (tr) {
              tx = parseFloat(tr[1]) || 0;
              ty = parseFloat(tr[2]) || 0;
            }
          }
          if (tx || ty) {
            el.style.left = `${Math.round(tx)}px`;
            el.style.top = `${Math.round(ty)}px`;
            el.style.transform = 'none';
            el.style.transition = 'none';
            el.style.willChange = 'auto';
          }
        }
      }
      rafId = requestAnimationFrame(applyCrispPosition);
    };
    rafId = requestAnimationFrame(applyCrispPosition);

    const handleClick = (event) => {
      const popover =
        document.querySelector('[data-tour-elem="popover"]') ||
        document.querySelector('.reactour__popover');
      const isClickInsidePopover = popover && popover.contains(event.target);

      const highlightedElement = document.querySelector('.ea-tour-highlight');
      const isClickOnHighlighted =
        highlightedElement && highlightedElement.contains(event.target);

      const isTourNavigation =
        event.target.closest('[data-tour-elem]') ||
        event.target.closest('.reactour__navigation') ||
        event.target.closest('.reactour__close') ||
        event.target.closest('.reactour__dots');

      if (!isClickInsidePopover && !isClickOnHighlighted && !isTourNavigation) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('click', handleClick, true);
    };
  }, [isOpen, setIsOpen]);

  return null;
}

function CrispPopover({ children, position, styles: popoverStyles }) {
  // Reactour may give different shapes — handle all safely
  const left =
    Math.round(
      position?.left ??
      position?.x ??
      popoverStyles?.left ??
      0
    );

  const top =
    Math.round(
      position?.top ??
      position?.y ??
      popoverStyles?.top ??
      0
    );

  return (
    <div
      className="reactour__popover"
      data-crisp="1"
      style={{
        position: "fixed",
        left,
        top,
        // transform: "none",
        transition: "none",
        maxWidth: 353,
        backgroundColor: "#fff",
        boxShadow: "0px 0.5em 3em rgba(0,0,0,0.3)",
        padding: "34px 30px 24px",
        zIndex: 22000,
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "geometricPrecision",
        overflow: "visible", 
      }}
    >
      {children}
    </div>
  );
}

// ---------- Main exported provider ----------
const TourBadgeChip = () => {
  const { currentStep } = useTour();
  const stepNo = Number.isFinite(currentStep) ? currentStep + 1 : 1;

  return (
    <Chip
      label={stepNo}
      variant="filled"
      style={{
        backgroundColor: chipSampleColors['0'],
        color: chipSampleColors['1'],
        height: "20px",
      }}
      sx={{
        ...sharedChipSx,
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 22001,
      }}
    />
  );
};

export default function AppTourProvider({ children }) {
  return (
    <TourProvider
      steps={[]}
      showPrevNextButtons
      showCloseButton
      showDots
      showBadge
      scrollSmooth
      disableInteraction={false}
      styles={styles}
      components={{
        Popover: CrispPopover, 
        Badge: TourBadgeChip,
        Navigation: ({ steps, currentStep, setCurrentStep, setIsOpen }) => (
          <CustomNavigation
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            setIsOpen={setIsOpen}
          />
        ), 
      }}
    >
      <TourClickHandler />
      <TourLauncherProvider>
        {children}
      </TourLauncherProvider>
    </TourProvider>
  );
}


